import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { API_URLS } from '../../../constants/api-urls';
import './quotation_qa_request_success.scss';

export default function QuotationQARequestSuccess() {
  const { quotationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const qaRequestId = searchParams.get('qa_request_id');
  const rfqId = searchParams.get('rfq_id');
  const pincode = searchParams.get('pincode');
  const city = searchParams.get('city');
  const state = searchParams.get('state');
  
  const [availableQAs, setAvailableQAs] = useState([]);
  const [qaMatchLevel, setQaMatchLevel] = useState(null); // 'pincode', 'city', or 'state'
  const [loadingQAs, setLoadingQAs] = useState(false);
  const [quotation, setQuotation] = useState(null);
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);

  useEffect(() => {
    fetchData();
  }, [quotationId, qaRequestId, pincode, city, state]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch quotation data
      if (quotationId) {
        const quotationResponse = await CommonService.get(`/quotation/${quotationId}`);
        if (quotationResponse && quotationResponse.success) {
          setQuotation(quotationResponse.data);
        }
      }
      
      // Fetch RFQ data
      if (rfqId) {
        const rfqResponse = await CommonService.get(`/rfq/${rfqId}`);
        if (rfqResponse && rfqResponse.success) {
          setRfq(rfqResponse.data);
        }
      }
      
      // Fetch available QAs based on location
      if (pincode || city || state) {
        await fetchAvailableQAs(pincode, city, state);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableQAs = async (pincode, city, state) => {
    try {
      setLoadingQAs(true);
      let inspectors = [];
      let matchLevel = null;

      console.log('Fetching QAs with location:', { pincode, city, state });

      // Priority 1: Try pincode match
      if (pincode) {
        try {
          const response = await CommonService.get(API_URLS.QA.QUALITY_INSPECTORS_BY_LOCATION(`?pincode=${pincode}`));
          console.log('Pincode match response:', response);
          const inspectorsData = response?.data?.data || response?.data || [];
          if (response && response.success && Array.isArray(inspectorsData) && inspectorsData.length > 0) {
            inspectors = inspectorsData;
            matchLevel = 'pincode';
            console.log(`Found ${inspectors.length} QAs by pincode`);
          } else {
            console.log('No QAs found by pincode, trying city...');
          }
        } catch (error) {
          console.error('Error fetching QAs by pincode:', error);
        }
      }

      // Priority 2: If no pincode match, try city match
      if (inspectors.length === 0 && city) {
        try {
          const response = await CommonService.get(API_URLS.QA.QUALITY_INSPECTORS_BY_LOCATION(`?city=${encodeURIComponent(city)}`));
          console.log('City match response:', response);
          const inspectorsData = response?.data?.data || response?.data || [];
          if (response && response.success && Array.isArray(inspectorsData) && inspectorsData.length > 0) {
            inspectors = inspectorsData;
            matchLevel = 'city';
            console.log(`Found ${inspectors.length} QAs by city`);
          } else {
            console.log('No QAs found by city, trying state...');
          }
        } catch (error) {
          console.error('Error fetching QAs by city:', error);
        }
      }

      // Priority 3: If no city match, try state match
      if (inspectors.length === 0 && state) {
        try {
          const response = await CommonService.get(API_URLS.QA.QUALITY_INSPECTORS_BY_LOCATION(`?state=${encodeURIComponent(state)}`));
          console.log('State match response:', response);
          const inspectorsData = response?.data?.data || response?.data || [];
          if (response && response.success && Array.isArray(inspectorsData)) {
            inspectors = inspectorsData;
            matchLevel = 'state';
            console.log(`Found ${inspectors.length} QAs by state`);
          } else {
            console.log('No QAs found by state');
          }
        } catch (error) {
          console.error('Error fetching QAs by state:', error);
        }
      }

      console.log('Final QAs result:', { count: inspectors.length, matchLevel });
      setAvailableQAs(inspectors);
      setQaMatchLevel(matchLevel);
    } catch (error) {
      console.error('Error fetching available QAs:', error);
      setAvailableQAs([]);
      setQaMatchLevel(null);
    } finally {
      setLoadingQAs(false);
    }
  };

  const handleSendRequestToInspectors = async () => {
    if (!qaRequestId) {
      toast.error('QA request ID is missing');
      return;
    }
    if (availableQAs.length === 0 && !pincode && !city && !state) {
      toast.error('No quality inspectors available for this location, or provide location (pincode, city or state)');
      return;
    }
    try {
      setSendingRequest(true);
      // Prefer exact list of inspectors shown on the page so broadcast notifies the same count
      const inspector_user_ids = availableQAs
        .map((qa) => qa.user_login_id ?? qa.user_id)
        .filter((id) => id != null && id > 0);
      const body = {};
      if (inspector_user_ids.length > 0) {
        body.inspector_user_ids = inspector_user_ids;
      } else if (pincode || city || state) {
        if (pincode) body.pincode = pincode;
        if (city) body.city = city;
        if (state) body.state = state;
      }
      const response = await CommonService.post(
        API_URLS.QA.QUOTATION_BROADCAST(qaRequestId),
        body
      );
      if (response && response.success) {
        const count = response?.data?.notified_count ?? 0;
        toast.success(
          count > 0
            ? `Request sent to ${count} quality inspector(s) successfully`
            : 'Broadcast completed. No inspectors matched the location.'
        );
        if (count > 0) await fetchAvailableQAs(pincode, city, state);
      } else {
        toast.error(response?.message || 'Failed to send request to quality inspectors');
      }
    } catch (error) {
      console.error('Error sending request to inspectors:', error);
      const msg = error?.response?.data?.message || error?.message || 'Failed to send request to quality inspectors';
      toast.error(msg);
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="quotation-qa-success">
        <div className="loading-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="quotation-qa-success">
      <div className="container">
        <div className="success-header">
          <div className="success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="32" fill="#10B981"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1>QA Request Submitted Successfully</h1>
          <p className="success-message">
            Your quality inspection request has been submitted and matched with Quality Inspectors based on location.
          </p>
        </div>

        {/* Location Matching Priority Info */}
        <div className="section-card location-matching-info-card">
          <div className="section-header">
            <div className="section-icon blue">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM11 14H9V12H11V14ZM11 10H9V6H11V10Z" fill="currentColor"/>
              </svg>
            </div>
            <h3>Quality Inspector Matching</h3>
          </div>
          <div className="matching-info-content">
            <p className="info-title">While listing Quality Inspectors, apply location-based priority matching in the following order:</p>
            <ol className="matching-priority-list">
              <li className={qaMatchLevel === 'pincode' ? 'active' : ''}>
                <span className="priority-number">1</span>
                <span className="priority-text">Match by inspection location <strong>pincode</strong></span>
                {qaMatchLevel === 'pincode' && (
                  <span className="match-badge">Matched</span>
                )}
              </li>
              <li className={qaMatchLevel === 'city' ? 'active' : ''}>
                <span className="priority-number">2</span>
                <span className="priority-text">If none are available, match by <strong>city</strong></span>
                {qaMatchLevel === 'city' && (
                  <span className="match-badge">Matched</span>
                )}
              </li>
              <li className={qaMatchLevel === 'state' ? 'active' : ''}>
                <span className="priority-number">3</span>
                <span className="priority-text">If still unavailable, match by <strong>state</strong></span>
                {qaMatchLevel === 'state' && (
                  <span className="match-badge">Matched</span>
                )}
              </li>
            </ol>
            
            {/* Available QAs Count */}
            {loadingQAs ? (
              <div className="qa-count-loading">Loading available Quality Inspectors...</div>
            ) : availableQAs.length > 0 ? (
              <div className="qa-count-display">
                <div className="qa-count-header">
                  <span className="qa-count-label">Available Quality Inspectors:</span>
                  <span className="qa-count-value">{availableQAs.length}</span>
                </div>
                <div className="qa-match-level">
                  Matched by: <strong>{qaMatchLevel === 'pincode' ? 'Pincode' : qaMatchLevel === 'city' ? 'City' : 'State'}</strong>
                </div>
              </div>
            ) : (
              <div className="qa-count-display no-qa">
                <span>No Quality Inspectors available in this location</span>
              </div>
            )}
            
            <p className="info-footer">Quality Inspectors will be displayed based on this priority logic.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            type="button"
            className="btn-primary"
            onClick={handleSendRequestToInspectors}
            disabled={sendingRequest || (availableQAs.length === 0 && !pincode && !city && !state)}
            style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
          >
            {sendingRequest ? 'Sending...' : 'Send request to quality inspectors'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/seller-quotation-list')}
          >
            Back to Quotations
          </button>
        </div>
      </div>
    </div>
  );
}
